/**
 * The handoff from sso.slr.family.
 *
 * Worth being precise about what the family portal is, because it is easy to
 * assume more: it is **not** an OIDC provider. It issues no tokens and holds
 * no session this app could adopt — Firebase Auth user pools are per project,
 * and SamuelSchool is its own project. The real identity provider is the
 * Google Workspace account behind every @slr.family address.
 *
 * What the portal hands over is a *hint*: the email of the Google account the
 * user is already signed into, as a `#sso_hint=` fragment. Fed back to Google
 * as `login_hint`, it completes sign-in with no prompt and no chooser, which
 * is what makes the portal feel like single sign-on. A fragment is the right
 * carrier — it never reaches a server log or a Referer header.
 *
 * The hint is captured at module load, which is the last moment it is still on
 * the URL, and it is tried exactly once. A stale hint — revoked access, the
 * wrong Google account — must leave the user on the sign-in screen rather than
 * bouncing them between here and Google forever.
 *
 * This module imports nothing, so it can be loaded before Firebase without a
 * cycle.
 */

const HINT_KEY = 'ss.sso.hint';
const ATTEMPT_KEY = 'ss.sso.attempted';

function session(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    // Private mode or an embedded context. We lose the silent path, not the
    // sign-in button.
    return null;
  }
}

/** Read `#sso_hint=`, stash it, and strip it from the address bar. */
function captureSsoHint(): void {
  if (typeof window === 'undefined') return;

  const match = window.location.hash.match(/[#&]sso_hint=([^&]+)/);
  if (!match || !match[1]) return;

  try {
    session()?.setItem(HINT_KEY, decodeURIComponent(match[1]));
  } catch {
    /* nothing to do — the button still works */
  }

  window.history.replaceState(null, '', window.location.pathname + window.location.search);
}

export function getSsoHint(): string | null {
  return session()?.getItem(HINT_KEY) ?? null;
}

export function clearSsoHint(): void {
  const store = session();
  store?.removeItem(HINT_KEY);
  store?.removeItem(ATTEMPT_KEY);
}

/** True only the first time. See the note about loops above. */
export function shouldTrySilentSignIn(): boolean {
  const store = session();
  if (!store) return false;
  return !!store.getItem(HINT_KEY) && !store.getItem(ATTEMPT_KEY);
}

export function markSilentSignInAttempted(): void {
  session()?.setItem(ATTEMPT_KEY, '1');
}

/**
 * Where to send someone after a deliberate sign-out.
 *
 * `?signout=1` ends the portal's session too. Without it the portal finds a
 * live session, bounces to its dashboard, and hands the hint straight back —
 * so "Sign out" would appear to do nothing at all.
 */
export const PORTAL_SIGNOUT_URL = `${
  import.meta.env.VITE_SSO_PORTAL_URL ?? 'https://sso.slr.family'
}/?signout=1`;

// Run at import time: the fragment is still on the URL only right now.
captureSsoHint();
